import { ElementTextBlockPositionType, HeaderPositionType, ShowFooterType, TrayType } from "common/constants";
import * as geom from "js/core/utilities/geom";
import React from "reactn";
import log from "js/core/logger";
import { app } from "js/namespaces";

import { BaseElement } from "../BaseElement";
import { DocContainerElement } from "./DocContainerElement";
import { AnnotationLayer } from "../../elements/AnnotationLayer";
import { CanvasBackground } from "../CanvasBackground";

export class DocumentElement extends BaseElement {
    constructor(props) {
        super(props);

        this.refFilters = React.createRef();
    }

    get isSelected() {
        return true;
    }

    getSelectableParent() {
        return this;
    }

    get alwaysRefreshStyles() {
        return false;
    }

    get headerPosition() {
        if (this.template.allowHeader) {
            return this.model.layout.headerPosition || HeaderPositionType.TOP;
        } else {
            return HeaderPositionType.NONE;
        }
    }

    get headerWidth() {
        return this.model.layout.headerWidth || 400;
    }

    get canShowFooter() {
        return this.template.allowFooter && this.trayLayout != TrayType.BOTTOM_TRAY && this.model.layout.elementTextBlockPosition != ElementTextBlockPositionType.TRAY;
    }

    get showFooter() {
        if (!this.canShowFooter) return false;

        // if the layout showFooter was set, always show the footer
        if (this.model.layout.showFooter == ShowFooterType.ON) {
            return true;
        } else if (this.model.layout.showFooter == ShowFooterType.OFF) {
            return false;
        } else {
            // otherwise use the theme's showFooterByDefault property UNLESS the template is set to preventDefaultFooter
            return app.currentTheme.get("showFooterByDefault") == true && this.template.preventDefaultFooter == false;
        }
    }

    get elementTextBlockPosition() {
        return this.model.layout.elementTextBlockPosition || ElementTextBlockPositionType.NONE;
    }

    get showAttribution() {
        return Boolean(this.model.layout.showElementAttribution);
    }

    get trayLayout() {
        try {
            if (!this.model.layout) {
                log.info(log.LAYOUT)(this.model.id);
                throw new Error("Cannot open presentation due to an error. Please refresh your browser.");
            }
            let trayLayout = this.model.layout.trayLayout || this.template.defaultTrayLayout || TrayType.NONE;
            if (trayLayout == TrayType.NONE || this.template.availableTrayLayouts.contains(trayLayout)) {
                return trayLayout;
            } else {
                log.warn("Invalid tray layout defined for this slide - " + trayLayout + " is not permitted for a " + this.template.constructor.id + " template.");
                this.model.layout.trayLayout = TrayType.NONE;
                return TrayType.NONE;
            }
        } catch (err) {
            console.log("Current slide: ", this.model.id, "Type: ", this.model.template_id);
            log.error(err);
            return TrayType.NONE;
        }
    }

    get maxTrayWidth() {
        if (this.elements.primary) {
            return this.canvas.CANVAS_WIDTH - this.elements.primary.minWidth;
        } else {
            return this.canvas.CANVAS_WIDTH / 2;
        }
    }

    get minTrayWidth() {
        return 200;
    }

    get maxTrayHeight() {
        if (this.elements.primary) {
            return this.canvas.CANVAS_HEIGHT - this.elements.primary.minHeight;
        } else {
            return this.canvas.CANVAS_HEIGHT / 2;
        }
    }

    get minTrayHeight() {
        return 200;
    }

    refreshElement(transition) {
        // We don't want to reach the top of the tree for this function, in this case you should
        // call canvas.refreshCanvas() instead.
        throw new Error("Can not refresh the whole canvas, use canvas.refreshCanvas() insted");
    }

    get canRefreshElement() {
        return false;
    }

    get childrenLayers() {
        return {
            background: 0,
            header: this.overlay ? 9 : 1,
            tray: 2,
            primary: 3,
            footer: 4,
            elementTextBlock: 5,
            elementAttribution: 6,
            annotations: 7,
            overlay: 8
        };
    }

    _build() {
        this.background = this.addElement("background", () => CanvasBackground, {
            model: {
                backgroundColor: this.model.layout.backgroundColor,
                backgroundStyle: this.model.layout.backgroundStyle
            }
        });

        if (!this.model.elements.primary) {
            this.model.elements.primary = {};
        }
        // this.primary = this.addElement("primary", () => ElementManager.get(this.template.elementType), { model: this.model.elements.primary });
        this.primary = this.addElement("primary", () => DocContainerElement, { model: this.model.elements.primary });

        if (!this.model.elements.annotations) {
            this.model.elements.annotations = {};
        }
        this.annotations = this.addElement("annotations", () => AnnotationLayer, { model: this.model.elements.annotations });
    }

    _calcProps(props, options) {
        let { size } = props;

        let canvasBounds = new geom.Rect(0, 0, size);

        this.background.calcProps(canvasBounds.size);
        let bodyBounds = canvasBounds.clone();

        let canvasMargin = this.primary.getCanvasMargins();

        let primaryBounds = bodyBounds.deflate(canvasMargin);

        let primaryProps = this.primary.calcProps(primaryBounds.size);
        primaryProps.bounds = primaryBounds;

        let annotationsProps = this.annotations.calcProps(canvasBounds.size);
        annotationsProps.bounds = canvasBounds;

        // Assigning correct layers to the children
        Object.entries(this.elements).forEach(([id, element]) => {
            element.calculatedProps.layer = this.childrenLayers[id];
        });

        return { size };
    }
}
